import React, { useCallback, useContext, useEffect } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { signInIdpSelectionUrl, signUpIdpSelectionUrl } from '@bingads-webui-campaign-react/pmax-landing-utils/src/paths';
import { AadEnforcementPage, type AadEnforcementBackStep, type AadEnforcementStep } from './aad-enforcement-page';
import { AadSignInRequiredPage, AadSwitchCompletePage } from './aad-switch-outcome-pages';
import { LoginErrorPage } from './login-error-page';
import {
  GoogleAccountRequiredPage,
  MicrosoftAccountRequiredPage,
  PersonalAccountRequiredPage,
  WorkAccountRequiredPage,
} from './provider-account-required-page';
import { type LoginErrorPage as LoginErrorPageName } from './login-error-log';
import { useLoginErrorLogger } from './use-login-error-logger';

export type LoginErrorReason = 'noAccount' | 'workAccountRequired' | 'personalAccountRequired' | 'googleAccountRequired' | 'microsoftAccountRequired' | 'aadEnforcement' | 'aadSwitchComplete' | 'aadSignInRequired';

type LoginErrorServerContext = {
  Urls?: {
    FederatedLogin?: string;
    PMaxLiteSignup?: string;
    SignOutUrl?: string;
    SwitchToWorkAccount?: string;
  };
};

// The page-load perf marker the hosting bundle started at bootstrap. Only `done` is used here.
type PagePerfMarker = { done: () => void };

export type RoutedLoginErrorPageProps = {
  perfMarker?: PagePerfMarker;
};

export const normalizeLoginErrorReason = (reason?: string | null): LoginErrorReason => {
  switch (reason?.toLowerCase()) {
    case 'workaccountrequired':
    case 'aadrequired':
      return 'workAccountRequired';
    case 'personalaccountrequired':
    case 'msarequired':
      return 'personalAccountRequired';
    case 'googleaccountrequired':
    case 'googlerequired':
      return 'googleAccountRequired';
    case 'microsoftaccountrequired':
    case 'microsoftrequired':
    case 'msasigninrequired':
      return 'microsoftAccountRequired';
    case 'aadenforcement':
    case 'aadonly':
      return 'aadEnforcement';
    case 'aadswitchcomplete':
    case 'workaccountswitched':
    case 'switchcomplete':
      return 'aadSwitchComplete';
    case 'aadsigninrequired':
    case 'credentialsdeactivated':
    case 'deactivated':
      return 'aadSignInRequired';
    default:
      return 'noAccount';
  }
};

export const buildFederatedLoginUrl = (
  federatedLoginUrl: string | undefined,
  idP?: 'AAD' | 'MSA' | 'Google',
  origin = window.location.origin
): string => {
  if (!federatedLoginUrl) {
    return '';
  }
  try {
    const url = new URL(federatedLoginUrl, origin);
    Array.from(url.searchParams.keys())
      .filter(key => key.toLowerCase() === 'idp')
      .forEach(key => url.searchParams.delete(key));
    if (idP) {
      url.searchParams.set('idP', idP);
    }
    return url.toString();
  } catch {
    return federatedLoginUrl;
  }
};

export const buildSignOutRedirectUrl = (
  signOutUrl: string | undefined,
  returnUrl: string | undefined,
  origin = window.location.origin
): string => {
  if (!returnUrl) {
    return '';
  }
  if (!signOutUrl) {
    return returnUrl;
  }

  try {
    const url = new URL(signOutUrl, origin);
    url.searchParams.set('RURL', returnUrl);
    return url.toString();
  } catch {
    return returnUrl;
  }
};

// Query params that are only meaningful on the login error page itself and should not be forwarded
// to downstream pages (signup, signin, etc.). All other params (e.g. s_cid, coupon tracking) are
// preserved so downstream pages can use them for attribution and coupon logic.
const LOGIN_ERROR_PARAMS = ['reason', 'scenario', 'email', 'username', 'customerName', 'customerNumber'];

export const buildForwardedSearch = (search = window.location.search): string => {
  const params = new URLSearchParams(search);
  LOGIN_ERROR_PARAMS.forEach(key => params.delete(key));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

// The login home page: the sign-in IdP selection page where the user picks a provider. Absolute
// so it works both as a direct navigation target ("Go back") and as a SignOutUrl RURL ("Try a different email").
export const buildLoginHomeUrl = (
  forwardedSearch = '',
  origin = window.location.origin
): string =>
  `${origin}${signInIdpSelectionUrl}${forwardedSearch}`;

export const buildSignUpUrl = (
  forwardedSearch = '',
  origin = window.location.origin
): string =>
  `${origin}${signUpIdpSelectionUrl}${forwardedSearch}`;

// The return URL for the post-migration "Continue to Microsoft Advertising" button. The server emits it
// under the platform-standard `RURL` key (MvcConstants.QueryReturnUrl == "RURL"), so match case-insensitively;
// `rurl`/`returnUrl` variants are also accepted.
export const getReturnUrlParam = (params: URLSearchParams): string | null => {
  const key = Array.from(params.keys()).find((k) => {
    const lower = k.toLowerCase();
    return lower === 'rurl' || lower === 'returnurl';
  });
  return key ? params.get(key) : null;
};

// The post-migration success page's "Continue to Microsoft Advertising" destination. The server
// embeds the original return URL as `rurl`. Only same-origin targets (relative paths or absolute
// URLs on the current origin) are honored to guard against an open redirect; anything else falls
// back to the login home page.
export const buildContinueToAdvertisingUrl = (
  rurl: string | null | undefined,
  forwardedSearch = '',
  origin = window.location.origin
): string => {
  if (rurl) {
    try {
      const url = new URL(rurl, origin);
      if (url.origin === origin) {
        return url.toString();
      }
    } catch {
      // Fall through to the login home page below.
    }
  }
  return buildLoginHomeUrl(forwardedSearch, origin);
};

// Resolves the concrete Microsoft IdP for the "use Microsoft sign-in for this email" card. The
// server may pass the exact identity it matched (`idP=AAD` or `idP=MSA`); when it does not, we
// default to MSA and let Microsoft's login broker personal-vs-work.
export const resolveMicrosoftIdP = (rawIdP?: string | null): 'AAD' | 'MSA' =>
  (rawIdP?.toUpperCase() === 'AAD' ? 'AAD' : 'MSA');

const reasonToPage: Record<LoginErrorReason, LoginErrorPageName> = {
  noAccount: 'NoAccount',
  workAccountRequired: 'WorkAccountRequired',
  personalAccountRequired: 'PersonalAccountRequired',
  googleAccountRequired: 'GoogleAccountRequired',
  microsoftAccountRequired: 'MicrosoftAccountRequired',
  aadEnforcement: 'AadEnforcement',
  aadSwitchComplete: 'AadSwitchComplete',
  aadSignInRequired: 'AadSignInRequired',
};

export const RoutedLoginErrorPage: React.FC<RoutedLoginErrorPageProps> = ({ perfMarker }) => {
  const serverContext = useContext(ServerContext) as LoginErrorServerContext;
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') || params.get('username');
  const rurl = getReturnUrlParam(params);
  const rawReason = params.get('reason') || params.get('scenario');
  const reason = normalizeLoginErrorReason(rawReason);
  const urls = serverContext?.Urls || {};
  const logEvent = useLoginErrorLogger();
  const page = reasonToPage[reason];
  const forwardedSearch = buildForwardedSearch();
  const switchAccountUrl = urls.SwitchToWorkAccount;

  useEffect(() => {
    logEvent(page, 'PageShown', { reason: rawReason || undefined, email: email || undefined });
  }, [logEvent, page, rawReason, email]);

  // Runs after the card is committed to the DOM, so the marker spans shell init through card render.
  // `done` is idempotent, so a re-render between reasons cannot double-report.
  useEffect(() => {
    if (perfMarker) {
      perfMarker.done();
    }
  }, [perfMarker]);

  const navigate = useCallback((returnUrl?: string) => {
    const targetUrl = buildSignOutRedirectUrl(urls.SignOutUrl, returnUrl);
    if (targetUrl) {
      window.location.assign(targetUrl);
    }
  }, [urls.SignOutUrl]);

  // Sign out first so a different identity can be used, then return to the login home page.
  const handleTryDifferentEmail = useCallback(() => {
    logEvent(page, 'ClickTryDifferentEmail', { reason: rawReason || undefined, email: email || undefined });
    navigate(buildLoginHomeUrl(forwardedSearch));
  }, [logEvent, page, rawReason, email, navigate, forwardedSearch]);

  const handleCreateAccount = useCallback(() => {
    logEvent(page, 'ClickCreateAccount', { reason: rawReason || undefined, email: email || undefined });
    window.location.assign(buildSignUpUrl(forwardedSearch));
  }, [logEvent, page, rawReason, email, forwardedSearch]);

  // The success page's continue button. The user is already migrated and signed in with AAD, so this
  // sends them to the server-provided return URL (guarded to same-origin) rather than back to sign-in.
  const handleContinueToAdvertising = useCallback(() => {
    logEvent(page, 'ClickContinueToAdvertising', { reason: rawReason || undefined, email: email || undefined });
    window.location.assign(buildContinueToAdvertisingUrl(rurl, forwardedSearch));
  }, [logEvent, page, rawReason, email, rurl, forwardedSearch]);

  const handleProviderSignIn = useCallback((idP: 'AAD' | 'MSA' | 'Google') => {
    logEvent(page, 'ClickContinue', { reason: rawReason || undefined, email: email || undefined, extra: { idP } });
    const targetUrl = buildFederatedLoginUrl(urls.FederatedLogin, idP);
    if (targetUrl) {
      window.location.assign(targetUrl);
    }
  }, [logEvent, page, rawReason, email, urls.FederatedLogin]);

  const handleGoBack = useCallback(() => {
    logEvent(page, 'ClickGoBack', { reason: rawReason || undefined, email: email || undefined });
    window.location.assign(buildLoginHomeUrl(forwardedSearch));
  }, [logEvent, page, rawReason, email, forwardedSearch]);

  const handleSelectHasWorkAccount = useCallback((hasWorkAccount: boolean) => {
    logEvent(page, 'ClickNext', { reason: rawReason || undefined, email: email || undefined, extra: { hasWorkAccount } });
  }, [logEvent, page, rawReason, email]);

  // Distinct from ClickGoBack: this "Go back" rewinds a step instead of leaving the page.
  const handleBackToChoice = useCallback((fromStep: AadEnforcementBackStep) => {
    logEvent(page, 'ClickBack', { reason: rawReason || undefined, email: email || undefined, extra: { fromStep } });
  }, [logEvent, page, rawReason, email]);

  // "Learn more" opens the help center in a new tab, so the user stays on the page. Logging it
  // separates "read the help article" from genuine abandonment in the outcome report.
  const handleLearnMore = useCallback((fromStep?: AadEnforcementStep) => {
    logEvent(page, 'ClickLearnMore', { reason: rawReason || undefined, email: email || undefined, extra: { fromStep } });
  }, [logEvent, page, rawReason, email]);

  // Hands control to the server-side AAD migration flow, which owns the MSA->AAD re-auth,
  // RegisterUserIdentity, and the confirmation page. The server builds this URL with `fromEmail`,
  // `rurl`, and `failUrl` already embedded, so navigate to it verbatim and never re-encode it.
  // There is no fallback: a plain AAD sign-in would not migrate the identity. A missing URL is a
  // server misconfiguration, so it is logged as an error and thrown for the page to surface.
  const handleSwitchAccount = useCallback(() => {
    logEvent(page, 'ClickSwitchAccount', { reason: rawReason || undefined, email: email || undefined });

    if (!switchAccountUrl) {
      logEvent(page, 'SwitchUrlMissing', { reason: rawReason || undefined, email: email || undefined, severity: 'error' });
      throw new Error('Cannot start the switch to a work or school account: ServerContext.Urls.SwitchToWorkAccount is missing.');
    }

    window.location.assign(switchAccountUrl);
  }, [logEvent, page, rawReason, email, switchAccountUrl]);

  switch (reason) {
    case 'workAccountRequired':
      return <WorkAccountRequiredPage email={email} onContinue={() => handleProviderSignIn('AAD')} onGoBack={handleGoBack} onLearnMore={handleLearnMore} />;
    case 'personalAccountRequired':
      return <PersonalAccountRequiredPage email={email} onContinue={() => handleProviderSignIn('MSA')} onGoBack={handleGoBack} onLearnMore={handleLearnMore} />;
    case 'googleAccountRequired':
      return <GoogleAccountRequiredPage email={email} onContinue={() => handleProviderSignIn('Google')} onGoBack={handleGoBack} onLearnMore={handleLearnMore} />;
    case 'microsoftAccountRequired':
      return <MicrosoftAccountRequiredPage email={email} onContinue={() => handleProviderSignIn(resolveMicrosoftIdP(params.get('idP')))} onGoBack={handleGoBack} onLearnMore={handleLearnMore} />;
    case 'aadEnforcement':
      return (
        <AadEnforcementPage
          onSwitchAccount={handleSwitchAccount}
          onSelectHasWorkAccount={handleSelectHasWorkAccount}
          onBackToChoice={handleBackToChoice}
          onGoBack={handleGoBack}
          onLearnMore={handleLearnMore}
        />
      );
    case 'aadSwitchComplete':
      return <AadSwitchCompletePage onContinue={handleContinueToAdvertising} />;
    case 'aadSignInRequired':
      return (
        <AadSignInRequiredPage
          onContinue={() => handleProviderSignIn('AAD')}
          onGoBack={handleGoBack}
          onLearnMore={() => handleLearnMore()}
        />
      );
    case 'noAccount':
    default:
      break;
  }

  return (
    <LoginErrorPage
      email={email}
      onTryDifferentEmail={handleTryDifferentEmail}
      onCreateAccount={handleCreateAccount}
    />
  );
};
